/**
 * MN Timeline — milestone nav with cross-fade + vertical slide panels.
 * Swipe the content left/right to move through milestones (mobile + touch).
 */
class MnTimeline extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.isAnimating = false;
    this.reduceMotion = null;
    this.abortController = null;
    this.animationTimeout = null;
    this.nav = null;
    this.stage = null;
    this.mobileQuery = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchTracking = false;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.pointerTracking = false;
    this.pointerId = null;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handlePortraitEnter = this.handlePortraitEnter.bind(this);
    this.handlePortraitLeave = this.handlePortraitLeave.bind(this);
  }

  connectedCallback() {
    this.cleanup();
    this.abortController = new AbortController();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 1199px)');

    this.triggers = [...this.querySelectorAll('[data-mn-timeline-trigger]')];
    this.panels = [...this.querySelectorAll('[data-mn-timeline-panel]')];
    this.items = [...this.querySelectorAll('.mn-timeline__item')];
    this.nav = this.querySelector('.mn-timeline__nav');
    this.stage = this.querySelector('[data-mn-timeline-stage]');
    this.portraits = [...this.querySelectorAll('.mn-timeline__portrait')];

    if (this.triggers.length === 0 || this.panels.length === 0) return;

    const activePanel = this.panels.find((panel) => panel.classList.contains('is-active'));
    this.activeIndex = activePanel
      ? Number(activePanel.dataset.index || 0)
      : 0;

    const { signal } = this.abortController;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleTriggerClick, { signal });
    });

    this.addEventListener('keydown', this.handleKeydown, { signal });

    // Swipe on milestone content — touch devices + mouse (theme editor preview).
    const swipeTarget = this.stage || this;
    swipeTarget.addEventListener('touchstart', this.handleTouchStart, { signal, passive: true });
    swipeTarget.addEventListener('touchmove', this.handleTouchMove, { signal, passive: true });
    swipeTarget.addEventListener('touchend', this.handleTouchEnd, { signal, passive: true });
    swipeTarget.addEventListener('touchcancel', this.handleTouchEnd, { signal, passive: true });
    swipeTarget.addEventListener('pointerdown', this.handlePointerDown, { signal });

    // Smooth image zoom in/out on hover (desktop).
    this.portraits.forEach((portrait) => {
      portrait.addEventListener('mouseenter', this.handlePortraitEnter, { signal });
      portrait.addEventListener('mouseleave', this.handlePortraitLeave, { signal });
    });

    this.scrollActiveIntoView(false);
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this.animationTimeout) {
      window.clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
    this.abortController?.abort();
    this.abortController = null;
    this.touchTracking = false;
    this.pointerTracking = false;
    this.pointerId = null;
  }

  handleTriggerClick(event) {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const nextIndex = Number(trigger.dataset.index);
    if (!Number.isFinite(nextIndex) || nextIndex === this.activeIndex || this.isAnimating) {
      return;
    }

    this.goTo(nextIndex);
  }

  readImageScale(image) {
    if (!(image instanceof HTMLElement)) return 1;
    const matrix = window.getComputedStyle(image).transform;
    if (!matrix || matrix === 'none') return 1;
    const match = matrix.match(/matrix\(([^)]+)\)/);
    if (!match) return 1;
    const values = match[1].split(',').map((value) => Number.parseFloat(value.trim()));
    return Number.isFinite(values[0]) ? values[0] : 1;
  }

  handlePortraitEnter(event) {
    if (this.reduceMotion?.matches) return;
    const portrait = event.currentTarget;
    if (!(portrait instanceof HTMLElement)) return;
    const image = portrait.querySelector('.mn-timeline__image');
    if (image instanceof HTMLElement) {
      image.style.transform = '';
    }
    portrait.classList.add('is-zooming');
  }

  handlePortraitLeave(event) {
    const portrait = event.currentTarget;
    if (!(portrait instanceof HTMLElement)) return;
    const image = portrait.querySelector('.mn-timeline__image');
    if (image instanceof HTMLElement) {
      const scale = this.readImageScale(image);
      image.style.transform = `scale(${scale})`;
      portrait.classList.remove('is-zooming');
      // Force reflow so the transition eases back from the frozen scale.
      void image.offsetWidth;
      requestAnimationFrame(() => {
        image.style.transform = '';
      });
      return;
    }
    portrait.classList.remove('is-zooming');
  }

  isSwipeFromNav(target) {
    return target instanceof Element && Boolean(target.closest('.mn-timeline__nav'));
  }

  applySwipe(dx, dy) {
    if (this.isAnimating) return;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (absX < 40 || absX < absY * 1.15) return;

    if (dx < 0) {
      this.goTo(Math.min(this.panels.length - 1, this.activeIndex + 1));
    } else {
      this.goTo(Math.max(0, this.activeIndex - 1));
    }
  }

  handleTouchStart(event) {
    if (!event.touches || event.touches.length !== 1) return;
    if (this.isSwipeFromNav(event.target)) {
      this.touchTracking = false;
      return;
    }

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchTracking = true;
  }

  handleTouchMove(event) {
    if (!this.touchTracking || !event.touches || event.touches.length !== 1) return;

    const dx = event.touches[0].clientX - this.touchStartX;
    const dy = event.touches[0].clientY - this.touchStartY;

    if (Math.abs(dy) > 24 && Math.abs(dy) > Math.abs(dx) * 1.1) {
      this.touchTracking = false;
    }
  }

  handleTouchEnd(event) {
    if (!this.touchTracking) return;
    this.touchTracking = false;

    const touch = event.changedTouches && event.changedTouches[0];
    if (!touch) return;

    this.applySwipe(touch.clientX - this.touchStartX, touch.clientY - this.touchStartY);
  }

  handlePointerDown(event) {
    // Touch is handled separately; this covers mouse drag in the theme editor.
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    if (this.isSwipeFromNav(event.target)) return;
    if (!(this.stage instanceof HTMLElement)) return;

    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.pointerTracking = true;
    this.pointerId = event.pointerId;

    this.stage.addEventListener('pointermove', this.handlePointerMove);
    this.stage.addEventListener('pointerup', this.handlePointerUp);
    this.stage.addEventListener('pointercancel', this.handlePointerUp);
  }

  handlePointerMove(event) {
    if (!this.pointerTracking || event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.pointerStartX;
    const dy = event.clientY - this.pointerStartY;

    if (Math.abs(dy) > 24 && Math.abs(dy) > Math.abs(dx) * 1.1) {
      this.pointerTracking = false;
    }
  }

  handlePointerUp(event) {
    if (event.pointerId !== this.pointerId) return;

    this.stage?.removeEventListener('pointermove', this.handlePointerMove);
    this.stage?.removeEventListener('pointerup', this.handlePointerUp);
    this.stage?.removeEventListener('pointercancel', this.handlePointerUp);

    if (!this.pointerTracking) {
      this.pointerId = null;
      return;
    }

    this.pointerTracking = false;
    this.pointerId = null;

    this.applySwipe(event.clientX - this.pointerStartX, event.clientY - this.pointerStartY);
  }

  handleKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches('[data-mn-timeline-trigger]')) {
      return;
    }

    const current = Number(target.dataset.index);
    if (!Number.isFinite(current)) return;

    let next = null;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      next = Math.min(this.triggers.length - 1, current + 1);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      next = Math.max(0, current - 1);
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = this.triggers.length - 1;
    }

    if (next === null || next === current) return;

    event.preventDefault();
    this.triggers[next]?.focus();
    this.goTo(next);
  }

  goTo(nextIndex) {
    if (!Number.isFinite(nextIndex) || nextIndex === this.activeIndex || this.isAnimating) {
      return;
    }

    const currentPanel = this.panels[this.activeIndex];
    const nextPanel = this.panels[nextIndex];
    if (!(currentPanel instanceof HTMLElement) || !(nextPanel instanceof HTMLElement)) return;

    // Update index immediately so chained swipes stay in sync.
    const previousIndex = this.activeIndex;
    this.activeIndex = nextIndex;

    this.updateNav(nextIndex);
    this.scrollActiveIntoView(true, nextIndex);

    if (this.reduceMotion?.matches) {
      this.setActiveImmediate(nextIndex);
      return;
    }

    this.isAnimating = true;

    currentPanel.classList.remove('is-active', 'is-entering');
    currentPanel.classList.add('is-leaving');
    currentPanel.setAttribute('inert', '');

    nextPanel.hidden = false;
    nextPanel.classList.remove('is-leaving');
    nextPanel.classList.add('is-entering');
    // Force reflow so the entering transform is applied before the active state.
    void nextPanel.offsetWidth;
    nextPanel.classList.add('is-active');
    nextPanel.removeAttribute('inert');

    if (this.animationTimeout) window.clearTimeout(this.animationTimeout);

    this.animationTimeout = window.setTimeout(() => {
      currentPanel.classList.remove('is-leaving');
      currentPanel.hidden = true;
      currentPanel.setAttribute('inert', '');

      nextPanel.classList.remove('is-entering');
      this.isAnimating = false;
      this.animationTimeout = null;

      // Safety: keep index if something interrupted mid-flight.
      if (this.activeIndex !== nextIndex && this.activeIndex === previousIndex) {
        this.activeIndex = nextIndex;
      }
    }, 480);
  }

  setActiveImmediate(nextIndex) {
    this.panels.forEach((panel, index) => {
      const isActive = index === nextIndex;
      panel.classList.toggle('is-active', isActive);
      panel.classList.remove('is-leaving', 'is-entering');
      panel.hidden = !isActive;
      if (isActive) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });
    this.activeIndex = nextIndex;
    this.isAnimating = false;
  }

  updateNav(nextIndex) {
    this.items.forEach((item, index) => {
      item.classList.toggle('is-active', index === nextIndex);
    });

    this.triggers.forEach((trigger, index) => {
      const isActive = index === nextIndex;
      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) {
        trigger.setAttribute('aria-current', 'true');
      } else {
        trigger.removeAttribute('aria-current');
      }
    });
  }

  scrollActiveIntoView(smooth, index = this.activeIndex) {
    if (!this.mobileQuery?.matches) return;
    const item = this.items[index];
    if (!(item instanceof HTMLElement)) return;

    item.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: smooth && !this.reduceMotion?.matches ? 'smooth' : 'auto',
    });
  }
}

if (!customElements.get('mn-timeline')) {
  customElements.define('mn-timeline', MnTimeline);
}
